/**
 * Payment Helper Functions
 *
 * Utility functions for payment-related operations
 */

/**
 * Format currency amount
 *
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (USD, EUR, GBP, etc.)
 * @return {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
    const symbols = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        JPY: '¥',
        CAD: 'C$',
        AUD: 'A$',
    };

    const symbol = symbols[currency] || currency + ' ';
    const formattedAmount = parseFloat(amount).toFixed(2);

    return `${symbol}${formattedAmount}`;
};

/**
 * Format date only (no time) for display
 *
 * @param {string} dateString - ISO date string
 * @return {string} Formatted date string (e.g. "Feb 11, 2026")
 */
export const formatDateOnly = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(date);
};

/**
 * Format date for display (with time)
 *
 * @param {string} dateString - ISO date string
 * @return {string} Formatted date string
 */
export const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

/**
 * Get payment status label
 *
 * @param {string} status - Payment status
 * @return {string} Human-readable status label
 */
export const getPaymentStatusLabel = (status) => {
    const labels = {
        pending: 'Pending',
        succeeded: 'Succeeded',
        completed: 'Completed',
        failed: 'Failed',
        refunded: 'Refunded',
        processing: 'Processing',
    };

    return labels[status] || status;
};

/**
 * Get payment status badge class
 *
 * @param {string} status - Payment status
 * @return {string} CSS class for status badge
 */
export const getPaymentStatusClass = (status) => {
    const classes = {
        pending: 'nb-bg-yellow-100 nb-text-yellow-800',
        succeeded: 'nb-bg-green-100 nb-text-green-800',
        completed: 'nb-bg-green-100 nb-text-green-800',
        failed: 'nb-bg-red-100 nb-text-red-800',
        refunded: 'nb-bg-gray-100 nb-text-gray-800',
        processing: 'nb-bg-blue-100 nb-text-blue-800',
    };

    return classes[status] || 'nb-bg-gray-100 nb-text-gray-800';
};

/**
 * Get gateway display name
 *
 * @param {string} gateway - Payment gateway identifier
 * @return {string} Human-readable gateway name
 */
export const getGatewayLabel = (gateway) => {
    const labels = {
        stripe: 'Stripe',
        paypal: 'PayPal',
        square: 'Square',
        manual: 'Manual',
    };

    return labels[gateway] || gateway;
};

/**
 * Shorten ID for display
 *
 * @param {string|number} id - ID to shorten
 * @param {number} length - Number of characters to show from start and end
 * @return {string} Shortened ID
 */
export const shortenId = (id, length = 4) => {
    const idString = String(id);
    if (idString.length <= length * 2) {
        return idString;
    }
    return `${idString.slice(0, length)}...${idString.slice(-length)}`;
};

/**
 * Copy text to clipboard
 *
 * @param {string} text - Text to copy
 * @return {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        } else {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            const success = document.execCommand('copy');
            document.body.removeChild(textArea);
            return success;
        }
    } catch (error) {
        return false;
    }
};

/**
 * Get payment amount from booking
 *
 * @param {Object} booking - Booking object
 * @return {number|null} Payment amount or null if free
 */
export const getBookingPaymentAmount = (booking) => {
    if (!booking) return null;

    // Check if payment exists
    if (booking.payment && booking.payment.amount) {
        return parseFloat(booking.payment.amount);
    }

    // Check booking meta
    if (booking.booking_meta && booking.booking_meta.payment_amount) {
        return parseFloat(booking.booking_meta.payment_amount);
    }

    return null;
};

/**
 * Get payment status from booking
 *
 * @param {Object} booking - Booking object
 * @return {string|null} Payment status or null if no payment
 */
export const getBookingPaymentStatus = (booking) => {
    if (!booking) return null;

    // Check if payment exists
    if (booking.payment && booking.payment.status) {
        return booking.payment.status;
    }

    // Infer from booking status for paid events
    const amount = getBookingPaymentAmount(booking);
    if (amount && amount > 0) {
        // If there's a payment amount but no payment record, status is pending
        return booking.booking_status === 'confirmed' ? 'completed' : 'pending';
    }

    return null;
};
